import React, { useMemo } from 'react'
import { BloodResultItem } from './types'
import { translate } from '../../selectors/translations'

import './BloodResultItemCard.scss'

export type BloodResultItemCardProps = {
  item: BloodResultItem
}

const LEFT_EDGE = 25
const RIGHT_EDGE = 75

const clamp = (n: number, min: number, max: number): number => Math.min(Math.max(n, min), max)

const computeMarkerPercent = (value: number, min: number, max: number): number => {
  if (!Number.isFinite(value)) {
    return LEFT_EDGE
  }

  const hasMin = Number.isFinite(min)
  const hasMax = Number.isFinite(max)

  // Handle one-sided ranges by synthesizing the missing bound for scaling.
  // This keeps the marker movement intuitive while still anchoring to the provided reference edge.
  if (hasMin && !hasMax) {
    const span = Math.abs(min) || 1
    return computeMarkerPercent(value, min, min + span)
  }

  if (!hasMin && hasMax) {
    const span = Math.abs(max) || 1
    return computeMarkerPercent(value, max - span, max)
  }

  if (!hasMin || !hasMax || max <= min) {
    return LEFT_EDGE
  }

  const normalSpan = max - min

  if (value <= min) {
    // Map [0..min] into [0..LEFT_EDGE]
    const denom = min === 0 ? 1 : min
    return clamp((value / denom) * LEFT_EDGE, 0, LEFT_EDGE)
  }

  if (value >= max) {
    // Map [max..max+normalSpan] into [RIGHT_EDGE..100]
    return clamp(RIGHT_EDGE + ((value - max) / normalSpan) * (100 - RIGHT_EDGE), RIGHT_EDGE, 100)
  }

  // Map [min..max] into [LEFT_EDGE..RIGHT_EDGE]
  return LEFT_EDGE + ((value - min) / normalSpan) * (RIGHT_EDGE - LEFT_EDGE)
}

const BloodResultItemCard: React.FC<BloodResultItemCardProps> = ({ item }) => {
  const status = item.status ?? 'unknown'
  const hasMin = Number.isFinite(item.referenceMin)
  const hasMax = Number.isFinite(item.referenceMax)

  const markerPercent = useMemo(
    () => computeMarkerPercent(item.value, item.referenceMin, item.referenceMax),
    [item.referenceMax, item.referenceMin, item.value],
  )

  const statusLabel =
    status === 'in_range'
      ? 'In range'
      : status === 'out_of_range'
        ? 'Out of range'
        : status === 'below_range'
          ? 'Below range'
          : 'Unknown'

  return (
    <div className="blood-result-item">
      <div className="blood-result-item__top">
        <div className="blood-result-item__name">{item.name}</div>
        <div className={`blood-result-item__pill blood-result-item__pill--${status}`}>
          <span className="blood-result-item__pill-dot" />
          {statusLabel}
        </div>
      </div>

      <div className="blood-result-item__value-row">
        <div className="blood-result-item__value">
          <span className="blood-result-item__value-number">{item.valueOriginal}</span>
          {!!item.unit && <span className="blood-result-item__value-unit"> {item.unit}</span>}
        </div>
      </div>

      <div className="blood-result-item__bar">
        <div className="blood-result-item__bar-track" aria-hidden="true">
          <div className="blood-result-item__bar-segment blood-result-item__bar-segment--left" />
          <div className="blood-result-item__bar-segment blood-result-item__bar-segment--mid" />
          <div className="blood-result-item__bar-segment blood-result-item__bar-segment--right" />
        </div>
        <div className={`blood-result-item__marker blood-result-item__marker--${status}`} style={{ left: `${markerPercent}%` }} />
        <div className="blood-result-item__caret" style={{ left: `${markerPercent}%` }} />
      </div>

      <div className="blood-result-item__range">
        {hasMin && hasMax && (
          <>
            <span className="blood-result-item__range-min">{item.referenceMin}</span>
            <span className="blood-result-item__range-max">{item.referenceMax}</span>
          </>
        )}
        {hasMin && !hasMax && (
          <span className="blood-result-item__range-min">≥ {item.referenceMin}</span>
        )}
        {!hasMin && hasMax && (
          <span className="blood-result-item__range-max">≤ {item.referenceMax}</span>
        )}
      </div>

      {item.note && (
        <div className="blood-result-item__comment">
          <div className="blood-result-item__comment-label">{translate('blood-results.comment')}:</div>
          <div className="blood-result-item__comment-text">{item.note}</div>
        </div>
      )}
    </div>
  )
}

export default BloodResultItemCard


